<template>
  <div
    ref="container"
    class="pill-tabs"
    :class="{
      'pill-tabs--small': small,
      'pill-tabs--no-wrap': !wrap
    }"
  >
    <div
      class="pill-tabs__highlight"
      :class="{ 'pill-tabs__highlight--ready': highlightReady }"
      :style="highlightStyle"
    />
    <button
      v-for="option in options"
      :key="option.value"
      ref="options"
      type="button"
      class="pill-tabs__option"
      :class="{
        'pill-tabs__option--active': option.value === value,
        'pill-tabs__option--small': small
      }"
      @click="$emit('input', option.value)"
    >
      <i
        v-if="option.icon"
        :class="option.icon"
        class="pill-tabs__icon"
      />
      <span class="pill-tabs__text">{{ option.text }}</span>
      <span
        v-if="option.count !== undefined"
        class="pill-tabs__count"
      >{{ option.count }}</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'PillTabs',
  props: {
    value: String,
    options: {
      type: Array,
      required: true,
    },
    small: {
      type: Boolean,
      default: false,
    },
    wrap: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      highlightStyle: {},
      highlightReady: false,
    };
  },
  watch: {
    value() {
      this.updateHighlight();
    },
    options() {
      this.$nextTick(this.updateHighlight);
    },
  },
  methods: {
    updateHighlight() {
      const index = this.options.findIndex(option => option.value === this.value);
      const button = this.$refs.options && this.$refs.options[index];

      if (index === -1 || !button) {
        this.highlightStyle = { ...this.highlightStyle, opacity: 0 };
        return;
      }

      this.highlightStyle = {
        opacity: 1,
        width: `${button.offsetWidth}px`,
        height: `${button.offsetHeight}px`,
        transform: `translate(${button.offsetLeft}px, ${button.offsetTop}px)`,
      };

      requestAnimationFrame(() => {
        this.highlightReady = true;
      });
    },
    handleResize() {
      setTimeout(this.updateHighlight, 10);
    },
  },
  mounted() {
    this.updateHighlight();
    window.addEventListener('resize', this.handleResize);

    if (this.$refs.container.parentElement) {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.$refs.container.parentElement);
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
};
</script>

<style lang="scss" scoped>
  .pill-tabs {
    position: relative;
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    padding: 4px;
    background-color: #f0f0f0;
    border-radius: 10px;

    &__highlight {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 0;
      border: 1px solid var(--pill-tabs-active-color, #464646);
      border-radius: 8px;
      background-color: #fff;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
      opacity: 0;
      pointer-events: none;

      &--ready {
        transition: transform 0.3s ease, width 0.3s ease, height 0.3s ease, opacity 0.3s ease;
      }
    }

    &__option {
      position: relative;
      z-index: 1;
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.4rem 0.8rem;
      border: 1px solid transparent;
      border-radius: 8px;
      background-color: transparent;
      color: #6e6e6e;
      font-size: 13px;
      white-space: nowrap;
      cursor: pointer;
      transition: color 0.3s;
      font-weight: 500;

      &:hover {
        color: #495057;
      }

      &--active,
      &--active:hover {
        color: var(--pill-tabs-active-color, #464646);
        cursor: default;
      }
    }

    &__count {
      font-size: 11px;
      opacity: 0.6;
    }

    &__text {
      display: inline-block;
      max-width: 110px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      vertical-align: middle;
    }

    &--small {
      padding: 3px;
      gap: 2px;
      border-radius: 7px;

      .pill-tabs__option {
        height: 26px;
        padding: 0 .5rem;
        font-size: 12px;
        border-radius: 5px;
      }

      .pill-tabs__highlight {
        border-radius: 5px;
      }
    }

    &--no-wrap {
      flex-wrap: nowrap;
      min-width: 0;
      max-width: 100%;
      overflow-x: auto;

      .pill-tabs__option {
        flex-shrink: 0;
      }
    }
  }
</style>
